import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fold, compactPostal, countryCodeOf, isPostalCodeRequired,
  loadCountryMeta, loadStates, loadCities, loadZipCodes, lookupPostalCode,
} from "./addressLocationData";

// One geographic logic for every address form:
//   Method A  Country -> State / Province -> City (ZIP optional while picking)
//   Method B  Country -> ZIP / postal code -> State + City filled in
// both in the same fields, in any order, always editable by hand.
//
// value: { country, state, stateCode, city, postalCode, latitude, longitude }
//
// Rules:
//  - a ZIP is never assumed to be one city: several places -> the user picks;
//  - a value typed by the user is never replaced silently: the ZIP only
//    overwrites a field that is empty or that was itself filled from the
//    data (a previous ZIP / the cascade); otherwise a suggestion is shown;
//  - changing the country clears the state / city / ZIP / coordinates;
//  - no data for the country, or the service is down -> plain text fields,
//    the address can always be completed by hand;
//  - strict (Target Location zones): city / ZIP only count when they exist
//    in the data, and choosing a level resets the levels below it.
export const EMPTY_LOCATION = {
  country: "", state: "", stateCode: "", city: "", postalCode: "", latitude: null, longitude: null,
};

const IDLE = { status: "idle", query: "", matches: [], partial: false };
const EMPTY_LIST = { loading: false, loaded: false, available: true, items: [], error: "" };

const sameState = (match, state) =>
  Boolean(state) && (fold(match.state) === fold(state) || (match.stateCode && fold(match.stateCode) === fold(state)));

// Loads one level of the cascade; a stale answer (country / state changed
// meanwhile) never overwrites the newer selection.
const useGeoList = (key, enabled, fetcher) => {
  const [list, setList] = useState(EMPTY_LIST);
  const seq = useRef(0);
  useEffect(() => {
    const current = ++seq.current;
    if (!enabled) {
      setList(EMPTY_LIST);
      return;
    }
    setList((prev) => ({ ...prev, loading: true, error: "" }));
    fetcher().then((data) => {
      if (current !== seq.current) return;
      if (!data || data.error) {
        setList({ loading: false, loaded: true, available: false, items: [], error: data?.error || "error" });
        return;
      }
      setList({ loading: false, loaded: true, available: data.available !== false, items: data.items || [], error: "" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  return list;
};

const useAddressLocation = ({ value, onChange, strict = false, debounceMs = 450 }) => {
  const country = countryCodeOf(value?.country);
  const v = { ...EMPTY_LOCATION, ...value, country };

  // Latest value, updated synchronously on every emit so two changes in a
  // row (or an answer arriving later) always build on the newest one.
  const valueRef = useRef(v);
  valueRef.current = v;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const emit = useCallback((changes) => {
    const next = { ...valueRef.current, ...changes };
    valueRef.current = next;
    onChangeRef.current?.(next);
  }, []);

  // Fields last filled FROM the data (ZIP lookup / unique ZIP of a city):
  // only these may be replaced automatically.
  const derived = useRef({ state: "", stateCode: "", city: "", postalCode: "" });
  const touched = useRef(false);

  // ── Country metadata + cascade lists ──
  const meta = useGeoList(country, Boolean(country), () => loadCountryMeta(country));
  const states = useGeoList(country, Boolean(country), () => loadStates(country));
  const serviceDown = Boolean(country) && (Boolean(meta.error) || Boolean(states.error));
  const noData = Boolean(country) && ((meta.loaded && !meta.available && !meta.error) || (states.loaded && !states.available && !states.error));
  const freeText = serviceDown || noData;

  const stateItem = useMemo(() => states.items.find((s) =>
    fold(s.name) === fold(v.state) || (s.code && fold(s.code) === fold(v.state)) || (v.stateCode && s.code && fold(s.code) === fold(v.stateCode))
  ), [states.items, v.state, v.stateCode]);
  const hasStates = states.items.length > 0;
  const stateReady = !hasStates || Boolean(stateItem);
  const cityQueryState = stateItem?.name || "";
  const cities = useGeoList(
    JSON.stringify([country, cityQueryState]),
    Boolean(country) && states.loaded && !freeText && stateReady,
    () => loadCities(country, cityQueryState)
  );
  const cityItem = useMemo(() => cities.items.find((c) => fold(c.name) === fold(v.city)), [cities.items, v.city]);
  const zipcodes = useGeoList(
    JSON.stringify([country, cityQueryState, cityItem?.name || ""]),
    Boolean(cityItem) && !freeText,
    () => loadZipCodes(country, cityQueryState, cityItem.name)
  );

  // ── ZIP / postal code lookup ──
  const [postal, setPostal] = useState(IDLE);
  const [postalInput, setPostalInput] = useState(v.postalCode || "");
  const lookupSeq = useRef(0);
  const timer = useRef(null);
  const ownPostal = useRef(v.postalCode || ""); // last postal code this hook wrote / saw typed
  useEffect(() => () => clearTimeout(timer.current), []);

  const applyMatches = useCallback((matches, partial, typed) => {
    const cur = valueRef.current;
    const d = derived.current;
    const changes = {};
    const stateNames = [...new Set(matches.map((m) => fold(m.state)).filter(Boolean))];
    const replaceState = strict || !cur.state || (d.state && (fold(cur.state) === fold(d.state) || fold(cur.state) === fold(d.stateCode)));
    const replaceCity = strict || !cur.city || (d.city && fold(cur.city) === fold(d.city));

    const agreeing = matches.find((m) => fold(m.city) === fold(cur.city) && (!cur.state || sameState(m, cur.state)));
    const chosen = agreeing || (matches.length === 1 && (!partial || strict) ? matches[0] : null);
    const next = { state: "", stateCode: "", city: "" };

    if (stateNames.length === 1) {
      const s = matches[0];
      next.state = s.state;
      next.stateCode = s.stateCode || "";
      if (replaceState) {
        changes.state = s.state;
        changes.stateCode = s.stateCode || "";
      }
    }
    if (chosen) {
      next.city = chosen.city;
      if (replaceCity || fold(cur.city) === fold(chosen.city)) changes.city = chosen.city;
      if (replaceState && !changes.state) {
        changes.state = chosen.state;
        changes.stateCode = chosen.stateCode || "";
      }
    } else if (replaceCity && cur.city && !matches.some((m) => fold(m.city) === fold(cur.city))) {
      // The city came from the previous code and is not a place of this one.
      changes.city = "";
    }
    const city = changes.city !== undefined ? changes.city : cur.city;
    const coords = matches.find((m) => city && fold(m.city) === fold(city)) || (matches.length === 1 ? matches[0] : null);
    changes.latitude = coords?.latitude ?? null;
    changes.longitude = coords?.longitude ?? null;
    if (strict) {
      changes.postalCode = partial ? matches[0].zipcode : (matches[0].zipcode || typed);
      ownPostal.current = changes.postalCode;
      setPostalInput(changes.postalCode);
    }
    derived.current = { ...next, postalCode: "" };
    emit(changes);
  }, [emit, strict]);

  // Matches of a code that was already there (address being edited): they
  // only tell which fields agree with the data -- nothing is changed.
  const seedFromMatches = useCallback((matches) => {
    const cur = valueRef.current;
    const agreeing = matches.find((m) => fold(m.city) === fold(cur.city)) || null;
    const stateMatch = matches.find((m) => sameState(m, cur.state)) || null;
    derived.current = {
      state: stateMatch ? cur.state : "",
      stateCode: stateMatch?.stateCode || "",
      city: agreeing ? cur.city : "",
      postalCode: "",
    };
  }, []);

  const runLookup = useCallback(async (code, text, { apply }) => {
    const seq = ++lookupSeq.current;
    const compact = compactPostal(text);
    if (!code || !compact) {
      setPostal(IDLE);
      return;
    }
    const m = await loadCountryMeta(code);
    if (seq !== lookupSeq.current) return;
    if (!m || m.error) {
      setPostal({ ...IDLE, status: "error", query: text });
      return;
    }
    if (m.available === false) {
      setPostal({ ...IDLE, status: "unavailable", query: text });
      return;
    }
    const lengths = (m.zipLengths || []).filter(Number.isFinite);
    const minLength = lengths.length ? Math.min(...lengths) : 1;
    const maxLength = lengths.length ? Math.max(...lengths) : 1;
    if (compact.length < minLength) {
      setPostal({ ...IDLE, query: text });
      return;
    }
    setPostal({ ...IDLE, status: "loading", query: text });
    const data = await lookupPostalCode(code, text);
    if (seq !== lookupSeq.current || countryCodeOf(valueRef.current.country) !== code) return;
    if (!data || data.error) {
      setPostal({ ...IDLE, status: "error", query: text });
      return;
    }
    const matches = data.matches || [];
    if (!matches.length) {
      setPostal({ ...IDLE, status: compact.length >= maxLength ? "notFound" : "idle", query: text });
      if (apply && !strict) {
        // Coordinates of the previous code no longer describe this address.
        const cur = valueRef.current;
        const city = cities.items.find((c) => fold(c.name) === fold(cur.city));
        emit({ latitude: city?.latitude ?? null, longitude: city?.longitude ?? null });
      }
      return;
    }
    setPostal({ status: "found", query: text, matches, partial: Boolean(data.partial) });
    if (apply) applyMatches(matches, Boolean(data.partial), text);
    else seedFromMatches(matches);
  }, [applyMatches, seedFromMatches, emit, strict, cities.items]);

  // A code that arrives from outside (address loaded for editing, form
  // reset) is looked up once, read-only.
  useEffect(() => {
    if (fold(v.postalCode) === fold(ownPostal.current) && postal.status !== "idle") return;
    if (fold(v.postalCode) === fold(ownPostal.current) && !v.postalCode) return;
    ownPostal.current = v.postalCode || "";
    setPostalInput(v.postalCode || "");
    clearTimeout(timer.current);
    if (v.postalCode && country) runLookup(country, v.postalCode, { apply: false });
    else {
      ++lookupSeq.current;
      setPostal(IDLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, v.postalCode]);

  // ── Actions ──
  const resetPostalLookup = () => {
    clearTimeout(timer.current);
    ++lookupSeq.current;
    setPostal(IDLE);
  };

  const setCountry = (raw) => {
    const code = countryCodeOf(raw);
    if (code === valueRef.current.country) return;
    touched.current = true;
    derived.current = { state: "", stateCode: "", city: "", postalCode: "" };
    ownPostal.current = "";
    setPostalInput("");
    resetPostalLookup();
    emit({ ...EMPTY_LOCATION, country: code });
  };

  const setState = (raw) => {
    const cur = valueRef.current;
    const item = states.items.find((s) => s.name === raw) || states.items.find((s) => fold(s.name) === fold(raw) || (s.code && fold(s.code) === fold(raw)));
    const name = item?.name ?? raw;
    if (name === cur.state && (item?.code || "") === (cur.stateCode || "")) return;
    touched.current = true;
    const changes = { state: name, stateCode: item?.code || "" };
    const d = derived.current;
    if (strict) {
      Object.assign(changes, { city: "", postalCode: "", latitude: null, longitude: null });
      ownPostal.current = "";
      setPostalInput("");
      resetPostalLookup();
    } else {
      // A city / ZIP that came from the data belonged to the previous state;
      // anything typed by the user stays (a mismatch is shown, not erased).
      if (cur.city && d.city && fold(cur.city) === fold(d.city)) changes.city = "";
      if (cur.postalCode && d.postalCode && fold(cur.postalCode) === fold(d.postalCode)) {
        changes.postalCode = "";
        ownPostal.current = "";
        setPostalInput("");
        resetPostalLookup();
      }
      changes.latitude = null;
      changes.longitude = null;
    }
    derived.current = { state: "", stateCode: "", city: changes.city === "" ? "" : d.city, postalCode: changes.postalCode === "" ? "" : d.postalCode };
    emit(changes);
  };

  const setCity = (text) => {
    touched.current = true;
    const match = cities.items.find((c) => fold(c.name) === fold(text));
    if (strict) {
      ownPostal.current = "";
      setPostalInput("");
      resetPostalLookup();
      emit(match
        ? { city: match.name, postalCode: "", latitude: match.latitude ?? null, longitude: match.longitude ?? null }
        : { city: "", postalCode: "", latitude: null, longitude: null });
      return text;
    }
    derived.current = { ...derived.current, city: "" };
    const zipMatch = postal.matches.find((m) => fold(m.city) === fold(text));
    const coords = zipMatch || match || null;
    emit({ city: text, latitude: coords?.latitude ?? null, longitude: coords?.longitude ?? null });
    return text;
  };

  const setPostalCode = (raw) => {
    const text = String(raw || "").toUpperCase();
    touched.current = true;
    setPostalInput(text);
    clearTimeout(timer.current);
    ++lookupSeq.current;
    derived.current = { ...derived.current, postalCode: "" };
    const code = valueRef.current.country;
    if (strict) {
      // Picked from the city's own list: committed at once (as before).
      const listed = zipcodes.items.find((z) => compactPostal(z.zipcode) === compactPostal(text));
      if (listed) {
        const cur = valueRef.current;
        ownPostal.current = listed.zipcode;
        setPostal({ status: "found", query: listed.zipcode, matches: [{ ...listed, city: cur.city, state: cur.state, stateCode: cur.stateCode || "" }], partial: false });
        emit({ postalCode: listed.zipcode, latitude: listed.latitude ?? null, longitude: listed.longitude ?? null });
        return;
      }
      if (valueRef.current.postalCode) {
        ownPostal.current = "";
        const city = cities.items.find((c) => fold(c.name) === fold(valueRef.current.city));
        emit({ postalCode: "", latitude: city?.latitude ?? null, longitude: city?.longitude ?? null });
      }
    } else {
      ownPostal.current = text;
      emit({ postalCode: text });
    }
    if (!compactPostal(text)) {
      setPostal(IDLE);
      return;
    }
    if (freeText && noData) {
      setPostal({ ...IDLE, status: "unavailable", query: text });
      return;
    }
    setPostal((prev) => ({ ...prev, status: "pending", query: text }));
    timer.current = setTimeout(() => runLookup(code, text, { apply: true }), debounceMs);
  };

  // One of several places of the same code.
  const pickMatch = (match) => {
    touched.current = true;
    derived.current = { state: match.state, stateCode: match.stateCode || "", city: match.city, postalCode: "" };
    const changes = { city: match.city, state: match.state, stateCode: match.stateCode || "", latitude: match.latitude ?? null, longitude: match.longitude ?? null };
    if (strict) {
      changes.postalCode = match.zipcode;
      ownPostal.current = match.zipcode;
    }
    emit(changes);
  };

  // City with exactly one ZIP code: proposed in the empty ZIP field (never
  // over a typed one, never while an address is only being displayed).
  useEffect(() => {
    if (strict || !touched.current || zipcodes.loading || zipcodes.items.length !== 1) return;
    const cur = valueRef.current;
    if (cur.postalCode || !cityItem) return;
    const only = zipcodes.items[0];
    ownPostal.current = only.zipcode;
    setPostalInput(only.zipcode);
    derived.current = { ...derived.current, postalCode: only.zipcode };
    setPostal({ status: "found", query: only.zipcode, matches: [{ ...only, state: cityQueryState || only.state, stateCode: stateItem?.code || "" }], partial: false });
    emit({ postalCode: only.zipcode, latitude: only.latitude ?? cur.latitude, longitude: only.longitude ?? cur.longitude });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipcodes.items, zipcodes.loading]);

  // ── What the form shows about the code ──
  const found = postal.status === "found" ? postal.matches : [];
  const cityAgrees = found.some((m) => fold(m.city) === fold(v.city));
  const mismatchStates = v.state && found.length && !found.some((m) => sameState(m, v.state))
    ? [...new Set(found.map((m) => m.state).filter(Boolean))]
    : [];
  // Several places (or only the wider area, e.g. Canada's 3-character
  // FSA) for this code and none chosen yet: the user picks.
  const choices = !cityAgrees && (found.length > 1 || (postal.partial && found.length === 1 && !strict)) ? found : [];
  const suggestion = !strict && found.length === 1 && !postal.partial && v.city && !cityAgrees && !mismatchStates.length
    ? found[0]
    : null;

  return {
    value: v,
    country,
    meta,
    states,
    cities,
    zipcodes,
    stateItem,
    cityItem,
    hasStates,
    stateReady,
    freeText,
    noData,
    serviceDown,
    postal,
    postalInput,
    choices,
    suggestion,
    mismatchStates,
    postalRequired: isPostalCodeRequired(country),
    stateRequired: Boolean(country) && !freeText && hasStates,
    setCountry,
    setState,
    setCity,
    setPostalCode,
    pickMatch,
  };
};

export default useAddressLocation;
